/**
 * spiceNextSession: the pure "next planned SPICE session" maths behind
 * the client record header's NEXT APPOINTMENT fact.
 *
 * Given a client's schedule (its effective hold and its sessions, the
 * shape the vitals-band aggregate carries once read through
 * effectiveHold) and today's date, finds the next occurrence at or after
 * today. Each session is read on its own frequency: a weekly session
 * counts every week; an every-two-weeks session counts only in its on
 * weeks (the Team page week grid's own isBiweeklyWeekOn rule, mirrored
 * here), and every week while no anchor is set, exactly like the week
 * grid; a monthly session counts only when its own weekday's date that
 * week is in its week of the month (weekOfMonthMatches). A session later
 * today still counts today. When every session in the current week has
 * passed, the search rolls forward a week at a time.
 *
 * A covered or paused plan carries no next date: its state is the fact
 * the caller shows instead of a date.
 *
 * Mirror of api/_shared/spiceNextSession.ts (no bridge between the api/
 * and src/ packages); keep the two in lockstep. The week-arithmetic
 * helpers below mirror api/_shared/capacity/week.ts (addDays,
 * isoWeekMonday, isBiweeklyWeekOn, weekOfMonthMatches).
 * weekOfMonthForDrop is the frontend's own: the week of the month a
 * monthly session takes when it is dragged onto a date.
 */

import type {
  SpiceHoldReason,
  SpiceSessionFrequency,
  SpiceSessionKind,
  SpiceWeekOfMonth,
} from './spiceVisitPlansApi';

export interface SpiceNextSessionSession {
  weekday: number; // 1..7, Monday..Sunday
  startTime: string; // 'HH:MM'
  kind: SpiceSessionKind;
  frequency: SpiceSessionFrequency;
  anchorWeek: string | null;
  weekOfMonth: SpiceWeekOfMonth | null;
}

export interface SpiceNextSessionSchedule {
  /** The effective state (effectiveHold), never a stored status word. */
  status: 'active' | 'covered' | 'on_hold';
  holdReason: SpiceHoldReason | null;
  statusUntil: string | null;
  sessions: readonly SpiceNextSessionSession[];
}

export type SpiceNextSessionResult =
  | { kind: 'session'; date: string; weekday: number; startTime: string; weekOfMonth: SpiceWeekOfMonth | null }
  | { kind: 'status'; status: 'covered' | 'on_hold'; holdReason: SpiceHoldReason | null; statusUntil: string | null }
  | { kind: 'none' };

// Eight weeks: past every every-two-weeks off week and every
// week-of-the-month gap (at most five weeks), small enough never to loop
// meaningfully.
const WEEKS_TO_SCAN = 8;

/** Add whole days to a YYYY-MM-DD date (UTC arithmetic on a zone-less date). */
function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const t = Date.UTC(y, m - 1, d) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/** The ISO Monday (YYYY-MM-DD) of the week containing `isoDate`. */
function isoWeekMonday(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun..6=Sat
  const back = dow === 0 ? 6 : dow - 1;
  return addDays(isoDate, -back);
}

/** True iff `week` (a Monday) is an "on" week counting from `anchor`
 *  (also a Monday): mirrors the Team page week grid's own rule so a
 *  client's "on" weeks agree everywhere. */
function isBiweeklyWeekOn(anchor: string, week: string): boolean {
  const [ay, am, ad] = anchor.split('-').map(Number) as [number, number, number];
  const [wy, wm, wd] = week.split('-').map(Number) as [number, number, number];
  const days = (Date.UTC(wy, wm - 1, wd) - Date.UTC(ay, am - 1, ad)) / 86_400_000;
  return days % 14 === 0;
}

/**
 * True iff `isoDate` falls in the given week of its month: first to fourth
 * by day of the month (1-7, 8-14, 15-21, 22-28), last for the month's
 * final seven days. In a month with four of a weekday, its fourth and its
 * last are the same day, and both words match it.
 */
export function weekOfMonthMatches(isoDate: string, which: SpiceWeekOfMonth): boolean {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  if (which === 'last') {
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return d + 7 > daysInMonth;
  }
  const n = which === 'first' ? 1 : which === 'second' ? 2 : which === 'third' ? 3 : 4;
  return Math.ceil(d / 7) === n;
}

const NUMBERED_WEEKS: readonly SpiceWeekOfMonth[] = ['first', 'second', 'third', 'fourth', 'last'];

/**
 * The week of the month a monthly session takes on `isoDate`: first to
 * fourth by day of the month, and last for a fifth occurrence. A session
 * that was the last keeps last while the date is in its month's final
 * seven days, so a last-Monday session moved to a fourth-and-last Tuesday
 * stays the last.
 */
export function weekOfMonthForDrop(isoDate: string, previous: SpiceWeekOfMonth | null): SpiceWeekOfMonth {
  if (previous === 'last' && weekOfMonthMatches(isoDate, 'last')) return 'last';
  const day = Number(isoDate.slice(8, 10));
  return NUMBERED_WEEKS[Math.ceil(day / 7) - 1]!;
}

/** Whether `session` falls in the week starting `week` (a Monday). */
function sessionFallsInWeek(session: SpiceNextSessionSession, week: string): boolean {
  if (session.frequency === 'monthly') {
    if (session.weekOfMonth === null) return true;
    return weekOfMonthMatches(addDays(week, session.weekday - 1), session.weekOfMonth);
  }
  if (session.frequency === 'weekly' || session.anchorWeek === null) return true;
  return isBiweeklyWeekOn(session.anchorWeek, week);
}

/**
 * The next planned session on or after `today` ('YYYY-MM-DD'), or the
 * reason there isn't one (a hold, or a schedule with no sessions at all,
 * an honest empty state, not an error).
 */
export function nextSpiceSession(
  schedule: SpiceNextSessionSchedule | null,
  today: string,
): SpiceNextSessionResult {
  if (schedule === null) return { kind: 'none' };
  if (schedule.status !== 'active') {
    return { kind: 'status', status: schedule.status, holdReason: schedule.holdReason, statusUntil: schedule.statusUntil };
  }
  if (schedule.sessions.length === 0) return { kind: 'none' };

  const startMonday = isoWeekMonday(today);
  for (let week = 0; week < WEEKS_TO_SCAN; week++) {
    const monday = addDays(startMonday, week * 7);
    let best: { date: string; weekday: number; startTime: string; weekOfMonth: SpiceWeekOfMonth | null } | null = null;
    for (const session of schedule.sessions) {
      if (!sessionFallsInWeek(session, monday)) continue;
      const date = addDays(monday, session.weekday - 1);
      if (date < today) continue; // already passed this week
      if (best === null || date < best.date || (date === best.date && session.startTime < best.startTime)) {
        best = { date, weekday: session.weekday, startTime: session.startTime, weekOfMonth: session.weekOfMonth };
      }
    }
    if (best !== null) return { kind: 'session', ...best };
  }
  return { kind: 'none' };
}
